import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  DrawerStepper,
  EditIcon,
  FlagIcon,
  Label,
  Select,
  showToast,
  Skeleton,
  Text,
} from '@mercai/clever'

import { CardInfo } from '@/components/card-info'

import { QUERIES_ADMIN, useSupplierOrderEdit } from '@/services'

import type { CardShipmentStatusProps } from './card-shipment-status.type'

import { SupplyOrderShipmentStatusEnum } from '@/types'

import { formatDate } from '@/helpers'
import styles from './card-shipment-status.module.css'

const CardShipmentStatusDefault = ({ data }: CardShipmentStatusProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplyOrderDetail')

  const [isOpen, setIsOpen] = useState(false)

  const [values, setValues] = useState<
    Partial<{
      shipmentStatus: keyof typeof SupplyOrderShipmentStatusEnum
    }>
  >({
    shipmentStatus: data?.shipmentStatus,
  })

  const queryClient = useQueryClient()

  const { mutateAsync } = useSupplierOrderEdit(data?.uuid)

  const isEditable = true

  const onChangeValues = (key: string, value: string | number) => {
    setValues((oldValues) => ({
      ...oldValues,
      [key]: value,
    }))
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const onSubmit = async () => {
    const response = await mutateAsync({
      uuid: data?.uuid,
      shipmentStatus: values?.shipmentStatus,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.SUPPLIER_ORDER.module, data?.uuid],
      })
      showToast({
        variant: 'success',
        title: t('tabGeneralCardShipmentStatusSuccessMessageTitle'),
        content: t('tabGeneralCardShipmentStatusSuccessMessageDescription'),
      })

      setIsOpen(false)
    } else {
      showToast({
        variant: 'error',
        title:
          response?.message ||
          t('tabGeneralCardShipmentStatusErrorMessageDescription'),
      })
    }
  }

  useEffect(() => {
    if (!isOpen) return setValues({})
    setValues({
      shipmentStatus: data?.shipmentStatus,
    })
  }, [isOpen])

  return (
    <div>
      <CardInfo
        icon={<FlagIcon />}
        title={t('tabGeneralCardShipmentStatusTitle')}
        className={styles.core}
        actions={
          isEditable && (
            <Button
              variant="ghost"
              className={styles.edit}
              size="small"
              onClick={() => setIsOpen(true)}
            >
              {t('editButton')}
              <EditIcon />
            </Button>
          )
        }
      >
        <div>
          <div>
            <Text variant="text-base">
              {data?.shipmentStatus
                ? tEnums(`supply_order_shipment_status_${data?.shipmentStatus}`)
                : t('notInformedLabel')}
            </Text>
          </div>
          <div>
            <Text>
              <strong>
                {t('tabGeneralCardShipmentStatusHistoryTitle')}
                {': '}
              </strong>
              {data?.shipmentStatus &&
              data?.shipmentStatusHistory?.[data?.shipmentStatus]
                ? formatDate(
                    data?.shipmentStatusHistory?.[data?.shipmentStatus],
                  )
                : t('notInformedLabel')}
            </Text>
          </div>
        </div>
      </CardInfo>

      <DrawerStepper
        isOpen={isOpen}
        onHandleOpen={handleClose}
        initialStep="root"
        title={t('tabGeneralCardShipmentStatusDrawerTitle')}
        translations={{
          optional: '',
        }}
        steps={{
          root: {
            backNavigate: 'root',
            isOptional: false,
            component: (
              <div className={styles['drawer-content']}>
                <div>
                  <Label>
                    {t('tabGeneralCardShipmentStatusDrawerShipmentStatusLabel')}
                  </Label>
                  <Select
                    allowEmpty
                    placeholder={t('selectPlaceholder')}
                    value={values?.shipmentStatus}
                    onChange={(val) =>
                      onChangeValues('shipmentStatus', val as string)
                    }
                    options={Object.entries(SupplyOrderShipmentStatusEnum).map(
                      ([key, value]) => ({
                        label: tEnums(`supply_order_shipment_status_${value}`),
                        value: key,
                      }),
                    )}
                  />
                </div>
              </div>
            ),
            footer: (
              <div className={styles['drawer-footer']}>
                <Button variant="secondary" onClick={handleClose}>
                  {t('cancelButton')}
                </Button>
                <Button variant="primary" onClick={onSubmit}>
                  {t('confirmButton')}
                </Button>
              </div>
            ),
          },
        }}
      />
    </div>
  )
}

const CardShipmentStatusLoading = () => {
  const { t } = useTranslation('screenSupplyOrderDetail')
  return (
    <CardInfo
      icon={<FlagIcon />}
      title={t('tabGeneralCardShipmentStatusTitle')}
      className={styles.core}
    >
      <Skeleton width="60%" height="1rem" />
    </CardInfo>
  )
}

export const CardShipmentStatus = (props: CardShipmentStatusProps) => {
  if (props.isLoading) {
    return <CardShipmentStatusLoading />
  }

  return <CardShipmentStatusDefault {...props} />
}
